import React, { useEffect, useState } from 'react';
import { Experience, Tool, AboutData } from '../types';
import { ContentService } from '../services/content';
import { EXPERIENCES as FALLBACK_EXP, TOOLS as FALLBACK_TOOLS } from '../constants';
import CometCard from '../components/CometCard';
const About: React.FC = () => {
  const [aboutData, setAboutData] = useState<AboutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const FALLBACK_PROFILE_IMAGE = 'https://res.cloudinary.com/dzpnljv5t/image/upload/v1769932244/Profile_Image_pmnfrd.jpg';
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await ContentService.getAboutData();
        if (data) {
          setAboutData(data);
        } else {
          setError('about_data returned no row.');
        }
      } catch (err) {
        setError('Connection failure to about_data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center" data-id="element-110">
        <div className="flex flex-col items-center gap-4" data-id="element-111">
          <div className="w-8 h-8 border-2 border-white/10 border-t-white rounded-full animate-spin" data-id="element-112" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-zinc-600 font-bold italic" data-id="element-113">
            accessing about_data...
          </span>
        </div>
      </div>;
  if (error && !aboutData) return <div className="min-h-screen bg-black flex items-center justify-center" data-id="element-114">
        <div className="text-center space-y-4" data-id="element-115">
          <p className="text-zinc-500 text-xl font-medium lowercase tracking-tight" data-id="element-116">
            {error}
          </p>
          <button onClick={() => window.location.reload()} className="text-[10px] uppercase tracking-widest font-bold text-white border border-white/20 px-6 py-2 rounded-full hover:bg-white hover:text-black transition-all" data-id="element-117">
            Retry
          </button>
        </div>
      </div>;
  const title = aboutData?.title || 'about';
  const heroBio = aboutData?.bio_hero || "i'm a product experience builder + design polymath";
  const detailedBio = aboutData?.bio_detailed || 'mixing ux, ui, prototypes, illustrations, motion, and storytelling to ship experiences that feel smooth, smart, and human. 😌⚡️';
  const profileImg = aboutData?.profile_image || FALLBACK_PROFILE_IMAGE;
  const experiences: Experience[] = aboutData?.experience_list && aboutData.experience_list.length > 0 ? aboutData.experience_list : FALLBACK_EXP;
  const tools: Tool[] = aboutData?.tools_list && aboutData.tools_list.length > 0 ? aboutData.tools_list : FALLBACK_TOOLS;
  return <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-[104px] md:pt-32 pb-24" data-id="element-118">
      <header className="mb-12 md:mb-32" data-id="element-119">
        <h1 className="text-5xl md:text-9xl font-extrabold tracking-tighter mb-12 md:mb-16 lowercase opacity-90" data-id="element-120">
          {title}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-start" data-id="element-121">
          <div className="lg:col-span-8 order-2 lg:order-1" data-id="element-122">
            <h2 className={`text-3xl md:text-6xl leading-[1.05] tracking-tighter font-medium mb-6 md:mb-8 lowercase ${!aboutData?.bio_hero ? 'text-zinc-500' : ''}`} data-id="element-123">
              {"I'm a product experience builder and a design polymath"}
            </h2>
            <p className={`text-xl md:text-3xl text-zinc-500 leading-tight tracking-tight max-w-4xl lowercase mb-12 md:mb-12`} data-id="element-124">
              {'mixing AI tools with, Research, UX/UI, Rapid Prototypes, illustrations, motion, and storytelling to ship experiences that feel smooth, smart, and human.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 border-t border-white/10 pt-12 md:pt-12" data-id="element-125">
              <div className="cursor-target group" data-id="element-126">
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-2 font-bold group-hover:text-white transition-colors" data-id="element-127">
                  .experience
                </span>
                <span className="text-2xl font-medium tracking-tight" data-id="element-128">
                  {aboutData?.experience_years || '12+ years'}
                </span>
              </div>
              <div className="cursor-target group" data-id="element-129">
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-2 font-bold group-hover:text-white transition-colors" data-id="element-130">
                  .location
                </span>
                <span className="text-2xl font-medium tracking-tight" data-id="element-131">
                  {aboutData?.location || 'Bengaluru'}
                </span>
              </div>
              <div className="cursor-target group" data-id="element-132">
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-2 font-bold group-hover:text-white transition-colors" data-id="element-133">
                  .status
                </span>
                <span className="text-2xl font-medium tracking-tight text-[#00FF5C]" data-id="element-134">
                  Building
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 order-1 lg:order-2 flex justify-start" data-id="element-135">
            <CometCard className="w-full max-w-[340px]" data-id="element-136">
              <div className="flex w-full flex-col items-stretch rounded-[16px] border-0 bg-[#1F2121] p-3 transition-all duration-700 hover:saturate-100 saturate-0 cursor-target group" data-id="element-137">
                <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[12px] bg-black" data-id="element-138">
                  <img src={profileImg} alt="Profile" className="w-full h-full object-cover contrast-125 grayscale group-hover:grayscale-0 transition-all duration-1000" data-id="element-139" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" data-id="element-140" />
                </div>
                <div className="mt-4 flex flex-shrink-0 items-center justify-between px-2 pb-2 font-mono text-white" data-id="element-141">
                  <div className="text-[10px] uppercase tracking-widest opacity-60" data-id="element-142">
                    SHYLASH
                  </div>
                  <div className="text-[10px] text-gray-400 opacity-40" data-id="element-143">
                    #DESIGNER
                  </div>
                </div>
              </div>
            </CometCard>
          </div>
        </div>
      </header>

      {/* MY JOURNEY */}
      <section className="mb-12 md:mb-48" data-id="element-144">
        <div className="mb-12 md:mb-20" data-id="element-145">
          <div className="flex items-center mb-6" data-id="element-146">
            <span className="text-[11px] text-zinc-500 uppercase tracking-[0.3em] font-black" data-id="element-147">
              .my journey
            </span>
            <div className="flex-grow border-b border-white/10 ml-6" data-id="element-148"></div>
          </div>
        </div>
        <div className="space-y-0" data-id="element-149">
          {experiences.map((exp, idx) => <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-10 md:py-16 border-b border-white/5 group hover:bg-white/[0.01] transition-all duration-500 px-6 md:px-8 -mx-6 md:-mx-8 cursor-target" data-id="element-150">
              <div className="md:col-span-3" data-id="element-151">
                <span className="text-2xl font-medium tracking-tight text-zinc-600 group-hover:text-zinc-400 transition-colors" data-id="element-152">
                  {exp.period}
                </span>
              </div>
              <div className="md:col-span-4" data-id="element-153">
                <h4 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3 text-white lowercase" data-id="element-154">
                  {exp.company}
                </h4>
                <p className="text-zinc-500 text-[10px] font-black uppercase tracking-[0.2em]" data-id="element-155">
                  {exp.role}
                </p>
              </div>
              <div className="md:col-span-5 md:pl-8" data-id="element-156">
                <p className="text-zinc-400 text-xl md:text-2xl leading-[1.4] tracking-tight font-medium opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-157">
                  {exp.description}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* TECH STACK */}
      <section className="mb-12 md:mb-48" data-id="element-158">
        <div className="mb-6 md:mb-12 flex items-center" data-id="element-159">
          <span className="text-[11px] text-zinc-500 uppercase tracking-[0.3em] font-black" data-id="element-160">
            .technologies
          </span>
          <div className="flex-grow border-b border-white/10 ml-6" data-id="element-161"></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-162">
          {tools.map((tool, idx) => <div key={idx} className="group p-6 md:p-10 bg-zinc-900/30 border border-white/5 rounded-sm hover:border-white/20 transition-all text-center cursor-target" data-id="element-163">
              <div className="mb-4 md:mb-6 flex items-center justify-center h-10 md:h-12" data-id="element-164">
                {tool.logoUrl ? <img src={tool.logoUrl} alt={tool.name} className={`h-8 md:h-10 w-auto object-contain group-hover:scale-125 transition-transform duration-700 ease-out${tool.name === 'Framer' || tool.name === 'Next.js' || tool.name === 'Vercel' ? ' invert' : ''}`} onError={e => {
              // fallback to emoji if logo fails to load
              const target = e.currentTarget;
              target.style.display = 'none';
              const span = target.nextElementSibling as HTMLElement;
              if (span) span.style.display = 'block';
            }} data-id="element-165" /> : null}
                <span className="text-4xl md:text-5xl group-hover:scale-125 transition-transform duration-700 ease-out" style={{
              display: tool.logoUrl ? 'none' : 'block'
            }} data-id="element-166">
                  {tool.icon}
                </span>
              </div>
              <h5 className="text-sm font-bold tracking-tight mb-1" data-id="element-167">
                {tool.name}
              </h5>
              <span className="text-[8px] text-zinc-500 uppercase tracking-widest font-extrabold" data-id="element-168">
                {tool.category}
              </span>
            </div>)}
        </div>
      </section>
    </div>;
};
export default About;